import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useState } from "react";
import TrendIndicator from "../../custom_dashboard/TrendIndicator";
import CustomLegendItem from "./CustomLegendItem";

const DonutPieChart = ({
  data = [],
  type = "entities",
  label = "Entities",
}) => {
  const [highlightedItem, setHighlightedItem] = useState(null);

  const totalValue = data.reduce((acc, item) => acc + item.value, 0);

  const totalDiff =
    data.reduce(
      (acc, item) =>
        acc +
        item?.trend?.[type]?.diff *
          (item?.trend?.[type]?.isPositive === true ? 1 : -1),
      0
    ) || 0;

  const averageTrend = {
    diff: totalDiff < 0 ? totalDiff * -1 : totalDiff,
    isPositive: totalDiff > 0,
    percentChange: totalDiff < 0 ? totalDiff * -1 : totalDiff,
  };

  // Process chart data to ensure small values are visible
  const chartData = data.map((item) => {
    const percentage = (item.value / totalValue) * 100;
    const minVisiblePercentage = 1; // Minimum 2% to be visible

    // If percentage is too small, boost it for visibility but keep original data
    const displayValue =
      percentage < minVisiblePercentage && percentage > 0
        ? (totalValue * minVisiblePercentage) / 100
        : item.value;

    return {
      id: item.id,
      value: displayValue,
      originalValue: item.value, // Keep original for calculations
      originalPercentage: percentage?.toFixed(1), // Store original percentage
      label: item.label,
      color: item.color,
      trend: item?.trend?.[type] || 0,
      isSmallValue: percentage < minVisiblePercentage && percentage > 0,
    };
  });

  return (
    <Box
      sx={{
        position: "relative",
        height: 208,
        display: "flex",
        alignItems: "center",
        gap: "40px",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "calc(100% - 150px)",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PieChart
          series={[
            {
              data: chartData,
              innerRadius: 75,
              outerRadius: 90,
              paddingAngle: 0.5, // Reduced padding to make small slices more visible
              startAngle: -90,
              endAngle: 270,
              cornerRadius: 2, // Slightly reduced for better small slice visibility
              highlightScope: {
                faded: "global",
                highlighted: "item",
              },
              id: "pie-segment",
              valueFormatter: (item) => {
                const chartItem = chartData.find((d) => d.id === item.id);
                if (!chartItem) return `${item.value}`;

                return `${chartItem.originalValue} `;
              },
            },
          ]}
          slotProps={{
            legend: {
              hidden: true,
            },
            pieArcLabel: {
              style: {
                fontSize: "10px",
                fill: "white",
                fontWeight: "bold",
              },
            },
            tooltip: {
              trigger: "item",
            },
          }}
          height={208}
          width={208}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          highlightedItem={highlightedItem}
          onHighlightChange={setHighlightedItem}
        />
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            width: "100%",
            maxWidth: 120,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* value */}
          <Typography variant="h5">
            {highlightedItem
              ? `${(
                  ((chartData.find(
                    (item) => item.id === highlightedItem.dataIndex
                  )?.originalValue || 0) /
                    totalValue) *
                  100
                ).toFixed(1)}%`
              : totalValue}
          </Typography>
          {/* label */}
          <Typography variant="body2">
            {highlightedItem
              ? chartData.find((item) => item.id === highlightedItem.dataIndex)
                  ?.label
              : "Total" + " " + label}
          </Typography>
          {/* trend indicator */}
          {highlightedItem ? (
            chartData.find((item) => item.id === highlightedItem.dataIndex)
              ?.trend ? (
              <TrendIndicator
                data={
                  chartData.find(
                    (item) => item.id === highlightedItem.dataIndex
                  )?.trend
                }
              />
            ) : null
          ) : averageTrend ? (
            <TrendIndicator data={averageTrend} />
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        {chartData.map((item) => (
          <CustomLegendItem
            key={item.id}
            item={item}
            highlightedItem={highlightedItem}
            onHover={setHighlightedItem}
          />
        ))}
      </Box>
    </Box>
  );
};

export default DonutPieChart;
